import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import Snackbar from './Snackbar';
import './ScheduleReview.css';

function ScheduleReview() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set page title
  useEffect(() => {
    document.title = 'CR Capability Index - Schedule Review';
  }, []);
  const assessment = location.state?.assessment || {
    name: '',
    email: '',
    role: ''
  };

  const [formData, setFormData] = useState({
    reviewDate: '',
    time: '',
    duration: '30',
    subject: 'Review Meeting',
    attendees: [assessment.email],
    additionalAttendees: '',
    schedule_comments: ''
  });

  // Handle date/time input changes
  const handleDateTimeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSchedule = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (!formData.reviewDate || !formData.time) {
        setSnackbar({
          show: true,
          message: 'Please fill in the review date and time',
          type: 'error'
        });
        return;
      }

      // Get current user email for organizer
      const userEmail = sessionStorage.getItem('user_emailId');
      if (!userEmail) {
        setSnackbar({
          show: true,
          message: 'User email not found. Please log in again.',
          type: 'error'
        });
        return;
      }

      // Get scheduled_by based on user role (must be 'manager' or 'employee')
      const userRole = sessionStorage.getItem('access_role') || 'user';
      const scheduledBy = userRole === 'manager' || userRole === 'admin' ? 'manager' : 'employee';

      // Get cycle_id from assessment
      const cycleId = assessment.cycleId;
      if (!cycleId) {
        setSnackbar({
          show: true,
          message: 'Cycle ID not found. Please try again.',
          type: 'error'
        });
        return;
      }

      // Create IST datetime string (user input is already in IST)
      const startDateTime = `${formData.reviewDate}T${formData.time}:00+05:30`;

      // Calculate end time in IST
      const [hours, minutes] = formData.time.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + parseInt(formData.duration);
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      const endTimeString = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      const endDateTime = `${formData.reviewDate}T${endTimeString}:00+05:30`;

      // Prepare attendees array
      const attendees = [...formData.attendees];
      if (formData.additionalAttendees.trim()) {
        // Split by comma and clean up emails
        const additionalEmails = formData.additionalAttendees
          .split(',')
          .map(email => email.trim())
          .filter(email => email.length > 0);
        attendees.push(...additionalEmails);
      }

      // Prepare API payload
      const payload = {
        startDateTime,
        endDateTime,
        subject: formData.subject,
        organizer: userEmail,
        attendees,
        scheduled_by: scheduledBy,
        cycle_id: cycleId,
        schedule_comments: formData.schedule_comments || ''
      };

      console.log('Scheduling meeting:', payload);

      // Make API call
      const response = await apiService.postData('schedule-team-meeting', payload);

      if (response && response.status === 'success') {
        setSnackbar({
          show: true,
          message: 'Meeting scheduled successfully!',
          type: 'success'
        });

        // Navigate after showing success message
        setTimeout(() => {
          navigate('/calibrationreview');
        }, 2000);
      } else {
        throw new Error(response?.message || 'Failed to schedule meeting');
      }

    } catch (error) {
      console.error('Error scheduling meeting:', error);
      setSnackbar({
        show: true,
        message: error.message || 'Failed to schedule meeting. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-review-container">
      {/* Header */}
      <div className="schedule-header">
        <button className="back-button" onClick={() => navigate('/calibrationreview')}>
          ← Back to Details
        </button>
        
        <div className="header-content">
          <h1>Schedule Calibration Review</h1>
          <p>Schedule a calibration session to discuss assessment discrepancies.</p>
        </div>
      </div>

      {/* Assessment Info Card */}
      <div className="assessment-info-card">
        <div className="associate-info">
          <h3>{assessment.email}</h3>
          <p>{assessment.role}</p>
        </div>
      </div>

      {/* Review Details Form */}
      <div className="review-details-form">
        <div className="form-header">
          <h3>Review Details</h3>
          <p>Configure the calibration review meeting details.</p>
        </div>

        <div className="form-grid">
          {/* Row 1 */}
          <div className="form-row">
            <div className="form-field">
              <label>Meeting Date *</label>
              <input
                type="date"
                value={formData.reviewDate}
                onChange={(e) => handleDateTimeChange('reviewDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-field">
              <label>Start Time (IST) *</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleDateTimeChange('time', e.target.value)}
                step="60"
                title="Select time in IST (Indian Standard Time)"
              />
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Time is in IST (Indian Standard Time)
              </small>
            </div>
          </div>

          {/* Row 2 */}
          <div className="form-row">
            <div className="form-field">
              <label>Duration (minutes) *</label>
              <select
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
              >
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
              </select>
            </div>

            <div className="form-field">
              <label>Meeting Subject *</label>
              <input
                type="text"
                placeholder="e.g., Calibration Review Meeting"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
              />
            </div>
          </div>

          {/* Attendees Section */}
          <div className="attendees-section">
            <label>Attendees *</label>
            <div className="attendees-list">
              <div className="attendee-item">
                <span className="person-icon">👤</span>
                <span>{assessment.email}</span>
              </div>
              <input
                type="text"
                placeholder="Add additional attendees (comma-separated emails)..."
                value={formData.additionalAttendees}
                onChange={(e) => handleInputChange('additionalAttendees', e.target.value)}
              />
            </div>
            <p className="helper-text">Include additional attendees as comma-separated email addresses.</p>
          </div>

          {/* Comments Section */}
          <div className="form-row">
            <div className="form-field full-width">
              <label>Comments</label>
              <textarea
                placeholder="Add any additional comments or notes for this meeting..."
                value={formData.schedule_comments}
                onChange={(e) => handleInputChange('schedule_comments', e.target.value)}
                rows="4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="schedule-actions">
        <button className="cancel-btn" onClick={() => navigate('/calibrationreview')} disabled={loading}>
          Cancel
        </button>
        <button className="schedule-btn" onClick={handleSchedule} disabled={loading}>
          {loading ? '⏳ Scheduling...' : '📅 Schedule Meeting'}
        </button>
      </div>

      {/* Snackbar notification */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={4000}
          onClose={() => setSnackbar({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
}

export default ScheduleReview;
